import {
  AnimatePresence,
  motion,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { isVideoUrl } from "../../lib/media";

export type DetailKind = "project" | "experience" | (string & {});

export type DetailPayload = {
  kind: DetailKind;
  title: string;
  filename: string;
  lines: string[];
  /** Primary / cover image (first of `images` when set) */
  image?: string;
  /** All preview images (cover + galleries). Falls back to `[image]`. */
  images?: string[];
  imageName?: string;
  imageBlur?: boolean;
};

type Phase = "idle" | "typing" | "output" | "done";

const spring = { type: "spring" as const, stiffness: 360, damping: 34 };

const KIND_DIR: Record<string, string> = {
  project: "projects",
  experience: "experience",
};

function folderFor(kind: DetailKind) {
  return KIND_DIR[kind] || String(kind);
}

function slugify(title: string, max = 40) {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9ก-๙]+/gi, "-")
      .replace(/^-|-$/g, "")
      .slice(0, max) || "detail"
  );
}

function slugFilename(title: string, kind: DetailKind) {
  return `${folderFor(kind)}/${slugify(title)}.md`;
}

function imageFilename(title: string, kind: DetailKind) {
  return `${folderFor(kind)}/${slugify(title, 36)}.png`;
}

function wrapText(text: string, width = 56): string[] {
  const words = text.trim().split(/\s+/);
  if (words.length === 0 || (words.length === 1 && !words[0])) return [""];
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > width && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

type CreateMacDetailOpts = {
  kind: DetailKind;
  title: string;
  description?: string;
  /** Key/value metadata shown under the title (issuer, date, etc.) */
  fields?: Record<string, string | undefined | null>;
  /** Optional links appended at the end */
  links?: Record<string, string | undefined | null>;
  tags?: string[];
  image?: string;
  /** Extra / gallery images (deduped with `image`) */
  images?: string[];
  imageBlur?: boolean;
};

function uniqImages(...lists: (string | undefined | null | string[])[]) {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of lists) {
    const arr = Array.isArray(item) ? item : item ? [item] : [];
    for (const url of arr) {
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

/** Build a payload for DetailTerminal from a project or experience. */
export function createMacDetail(opts: CreateMacDetailOpts): DetailPayload {
  const {
    kind,
    title,
    description,
    fields,
    links,
    tags,
    image,
    images,
    imageBlur,
  } = opts;

  const fieldLines = Object.entries(fields || {})
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `${k.padEnd(10)} ${v}`);

  const tagLine =
    tags && tags.length > 0 ? [`stack:     ${tags.join(", ")}`] : [];

  const linkLines = Object.entries(links || {})
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `${k}: ${v}`);

  const allImages = uniqImages(image, images);
  const primary = allImages[0];

  const galleryNote =
    allImages.length > 1 ? [`images:    ${allImages.length} files`] : [];

  const lines = [
    `# ${title}`,
    "",
    ...fieldLines,
    ...tagLine,
    ...galleryNote,
    ...(fieldLines.length || tagLine.length || galleryNote.length ? [""] : []),
    "## description",
    "",
    ...wrapText(description?.trim() || "(no description)"),
    ...(linkLines.length ? ["", ...linkLines] : []),
  ];

  return {
    kind,
    title,
    filename: slugFilename(title, kind),
    lines,
    image: primary,
    images: allImages,
    imageName: primary ? imageFilename(title, kind) : undefined,
    imageBlur,
  };
}

function PreviewGallery({
  images,
  title,
  blur,
  isDesktop,
}: {
  images: string[];
  title: string;
  blur?: boolean;
  isDesktop: boolean;
}) {
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const multi = images.length > 1;
  const current = images[Math.min(index, images.length - 1)] || images[0];

  useEffect(() => {
    setIndex(0);
  }, [images]);

  const go = (dir: -1 | 1) => {
    if (!multi) return;
    setIndex((i) => (i + dir + images.length) % images.length);
  };

  useEffect(() => {
    if (!multi) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setIndex((i) => (i - 1 + images.length) % images.length);
      }
      if (e.key === "ArrowRight") {
        setIndex((i) => (i + 1) % images.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [multi, images.length]);

  return (
    <>
      <div
        className={[
          "relative bg-[#1a1a1a] p-3 sm:p-4 flex items-center justify-center overflow-hidden select-none",
          isDesktop ? "min-h-[200px] max-h-[min(48vh,380px)]" : "min-h-[140px] max-h-[32vh]",
        ].join(" ")}
        onTouchStart={(e) => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (touchX.current == null || !multi) return;
          const dx = (e.changedTouches[0]?.clientX ?? 0) - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) < 40) return;
          go(dx < 0 ? 1 : -1);
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isVideoUrl(current) ? (
            <motion.video
              key={current}
              src={current}
              initial={{ opacity: 0.35, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0.35, x: -12 }}
              transition={{ duration: 0.18 }}
              className={[
                "max-w-full object-contain rounded-[4px] shadow-[0_8px_32px_rgba(0,0,0,0.45)]",
                isDesktop ? "max-h-[min(40vh,300px)]" : "max-h-[24vh]",
                blur ? "blur-md scale-105" : "",
              ].join(" ")}
              controls
              playsInline
              muted
              loop
              autoPlay
            />
          ) : (
            <motion.img
              key={current}
              src={current}
              alt={`${title} (${index + 1})`}
              initial={{ opacity: 0.35, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0.35, x: -12 }}
              transition={{ duration: 0.18 }}
              className={[
                "max-w-full object-contain rounded-[4px] shadow-[0_8px_32px_rgba(0,0,0,0.45)]",
                isDesktop ? "max-h-[min(40vh,300px)]" : "max-h-[24vh]",
                blur ? "blur-md scale-105" : "",
              ].join(" ")}
              draggable={false}
            />
          )}
        </AnimatePresence>

        {multi && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                go(-1);
              }}
              className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/45 text-white/90 text-[18px] leading-none hover:bg-black/60"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                go(1);
              }}
              className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/45 text-white/90 text-[18px] leading-none hover:bg-black/60"
            >
              ›
            </button>
          </>
        )}
      </div>

      {multi && (
        <div className="flex gap-1.5 px-3 py-2 overflow-x-auto bg-[#121212] border-t border-white/5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              aria-label={`Image ${i + 1}`}
              aria-current={i === index}
              onClick={(e) => {
                e.stopPropagation();
                setIndex(i);
              }}
              className={[
                "shrink-0 h-11 w-14 rounded-[4px] overflow-hidden border transition-opacity",
                i === index
                  ? "border-black/40 dark:border-white/55 opacity-100"
                  : "border-transparent opacity-55 hover:opacity-85",
              ].join(" ")}
            >
              {isVideoUrl(src) ? (
                <video
                  src={src}
                  muted
                  playsInline
                  preload="metadata"
                  className={["h-full w-full object-cover", blur ? "blur-sm" : ""].join(" ")}
                />
              ) : (
                <img
                  src={src}
                  alt=""
                  className={["h-full w-full object-cover", blur ? "blur-sm" : ""].join(" ")}
                  draggable={false}
                />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="px-3.5 py-2 flex items-center justify-between gap-3 text-[11px] text-black/50 dark:text-white/40 bg-[#ececec] dark:bg-[#2d2d2d] border-t border-black/5">
        <span className="truncate">{title}</span>
        {multi && (
          <span className="shrink-0 tabular-nums font-mono text-black/40 dark:text-white/35">
            {index + 1} / {images.length}
          </span>
        )}
      </div>
    </>
  );
}

function TrafficLights({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center gap-2 sm:gap-1.5">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="group relative h-3.5 w-3.5 sm:h-3 sm:w-3 rounded-full bg-[#ff5f57] after:absolute after:-inset-2 after:content-['']"
      >
        <span className="pointer-events-none absolute inset-0 hidden items-center justify-center text-[8px] font-bold text-black/55 group-hover:flex">
          ×
        </span>
      </button>
      <span className="h-3.5 w-3.5 sm:h-3 sm:w-3 rounded-full bg-[#febc2e]" />
      <span className="h-3.5 w-3.5 sm:h-3 sm:w-3 rounded-full bg-[#28c840]" />
    </div>
  );
}

function MacWindow({
  title,
  onClose,
  children,
  className = "",
  initialX = 0,
  initialY = 0,
  zIndex,
  onFocus,
  canDrag,
  constraintsRef,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  initialX?: number;
  initialY?: number;
  zIndex: number;
  onFocus: () => void;
  canDrag: boolean;
  constraintsRef?: RefObject<HTMLDivElement | null>;
}) {
  const x = useMotionValue(initialX);
  const y = useMotionValue(initialY);
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    x.set(initialX);
    y.set(initialY);
  }, [initialX, initialY, x, y]);

  return (
    <motion.div
      role="dialog"
      aria-label={title}
      onPointerDown={onFocus}
      initial={{ opacity: 0, y: 18, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: dragging ? 1.015 : 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.96 }}
      transition={spring}
      drag={canDrag}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0.08}
      dragConstraints={constraintsRef}
      onDragStart={() => setDragging(true)}
      onDragEnd={() => setDragging(false)}
      style={{ x: canDrag ? x : 0, y: canDrag ? y : 0, zIndex }}
      className={[
        "pointer-events-auto relative overflow-hidden rounded-[12px] border border-white/10",
        "shadow-[0_28px_90px_rgba(0,0,0,0.55)]",
        className,
      ].join(" ")}
    >
      <div
        className={[
          "flex items-center gap-2 border-b border-black/5 px-3.5 py-2.5",
          "bg-[#ececec] dark:bg-[#2d2d2d]",
          canDrag ? "cursor-grab active:cursor-grabbing touch-none select-none" : "",
        ].join(" ")}
        onPointerDown={(e) => {
          onFocus();
          if (canDrag) dragControls.start(e);
        }}
      >
        <TrafficLights onClose={onClose} />
        <span className="flex-1 text-center text-[11px] tracking-wide truncate px-2 text-black/45 dark:text-white/40 font-medium">
          {title}
        </span>
        <span className="w-[42px]" aria-hidden />
      </div>
      {children}
    </motion.div>
  );
}

export function DetailTerminal({
  detail,
  onClose,
}: {
  detail: DetailPayload | null;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [typedCmd, setTypedCmd] = useState("");
  const [visibleLines, setVisibleLines] = useState(0);
  const [focus, setFocus] = useState<"terminal" | "preview">("preview");
  const [isDesktop, setIsDesktop] = useState(false);
  const [termClosed, setTermClosed] = useState(false);
  const [previewClosed, setPreviewClosed] = useState(false);
  const deskRef = useRef<HTMLDivElement>(null);

  const command = detail ? `cat ${detail.filename}` : "";
  const gallery =
    detail?.images && detail.images.length > 0
      ? detail.images
      : detail?.image
        ? [detail.image]
        : [];
  const hasImage = gallery.length > 0;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!detail) {
      setPhase("idle");
      setTypedCmd("");
      setVisibleLines(0);
      setTermClosed(false);
      setPreviewClosed(false);
      return;
    }

    setPhase("idle");
    setTypedCmd("");
    setVisibleLines(0);
    setTermClosed(false);
    setPreviewClosed(false);
    setFocus(detail.image ? "preview" : "terminal");
    const start = setTimeout(() => setPhase("typing"), 280);
    return () => clearTimeout(start);
  }, [detail]);

  useEffect(() => {
    if (!detail || termClosed || phase !== "typing") return;

    if (typedCmd.length < command.length) {
      const t = setTimeout(() => {
        setTypedCmd(command.slice(0, typedCmd.length + 1));
      }, 28 + Math.random() * 24);
      return () => clearTimeout(t);
    }

    const next = setTimeout(() => setPhase("output"), 220);
    return () => clearTimeout(next);
  }, [phase, typedCmd, command, detail, termClosed]);

  useEffect(() => {
    if (!detail || termClosed || phase !== "output") return;

    if (visibleLines < detail.lines.length) {
      const t = setTimeout(() => setVisibleLines((n) => n + 1), 40);
      return () => clearTimeout(t);
    }

    const done = setTimeout(() => setPhase("done"), 100);
    return () => clearTimeout(done);
  }, [phase, visibleLines, detail, termClosed]);

  useEffect(() => {
    if (!detail) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [detail, onClose]);

  useEffect(() => {
    if (!detail) return;
    const bothGone = termClosed && (!hasImage || previewClosed);
    if (bothGone) onClose();
  }, [termClosed, previewClosed, hasImage, detail, onClose]);

  const imageTitle =
    gallery.length > 1
      ? `${detail?.title || "preview"} — ${gallery.length} images`
      : detail?.imageName || `${detail?.title || "preview"}.png`;

  return (
    <AnimatePresence>
      {detail && (
        <motion.div
          className="fixed inset-0 z-[10000]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            className="absolute inset-0 bg-[#0b0b0f]/70 backdrop-blur-[3px]"
            aria-label="Close"
            onClick={onClose}
          />

          {/* Mobile top bar */}
          {!isDesktop && (
            <div
              className="pointer-events-auto absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 pb-2"
              style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}
            >
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] px-3 rounded-full text-[14px] font-medium text-white/90"
                style={{ background: "rgba(255,255,255,0.12)" }}
              >
                Done
              </button>
              <span className="text-[12px] text-white/45 font-mono truncate max-w-[50%]">
                {detail.title}
              </span>
              <span className="w-[64px]" aria-hidden />
            </div>
          )}

          <div
            ref={deskRef}
            className={[
              "pointer-events-none absolute inset-0 flex",
              isDesktop
                ? "items-center justify-center p-6 md:p-10"
                : "items-stretch justify-center overflow-y-auto overscroll-contain px-3 pb-[max(16px,env(safe-area-inset-bottom))] pt-[calc(56px+env(safe-area-inset-top))]",
            ].join(" ")}
          >
            <div
              className={[
                "pointer-events-none relative w-full max-w-5xl",
                isDesktop
                  ? "md:block md:min-h-[min(72vh,560px)]"
                  : "flex flex-col gap-3 my-auto",
              ].join(" ")}
            >
              <AnimatePresence>
                {hasImage && !previewClosed && (
                  <MacWindow
                    key="preview"
                    title={imageTitle}
                    onClose={() => setPreviewClosed(true)}
                    onFocus={() => setFocus("preview")}
                    canDrag={isDesktop}
                    constraintsRef={deskRef}
                    initialX={isDesktop ? 40 : 0}
                    initialY={isDesktop ? -20 : 0}
                    zIndex={focus === "preview" ? 20 : 10}
                    className={[
                      "w-full",
                      isDesktop
                        ? "md:absolute md:left-0 md:top-0 md:w-[min(52%,420px)]"
                        : "shrink-0",
                      "bg-[#f5f5f7] dark:bg-[#1c1c1e]",
                    ].join(" ")}
                  >
                    <PreviewGallery
                      images={gallery}
                      title={detail.title}
                      blur={detail.imageBlur}
                      isDesktop={isDesktop}
                    />
                  </MacWindow>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {!termClosed && (
                  <MacWindow
                    key="terminal"
                    title={`charcrith — ${detail.filename}`}
                    onClose={() => setTermClosed(true)}
                    onFocus={() => setFocus("terminal")}
                    canDrag={isDesktop}
                    constraintsRef={deskRef}
                    initialX={isDesktop ? (hasImage ? 220 : 80) : 0}
                    initialY={isDesktop ? (hasImage ? 80 : 40) : 0}
                    zIndex={focus === "terminal" ? 20 : 10}
                    className={[
                      "w-full",
                      isDesktop
                        ? "md:absolute md:right-0 md:bottom-0 md:w-[min(58%,480px)]"
                        : "shrink-0",
                      "bg-[#1e1e1e]",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "overflow-y-auto px-4 py-3.5 font-mono text-[12px] sm:text-[13px] leading-relaxed text-[#d4d4d4]",
                        isDesktop
                          ? "max-h-[min(52vh,420px)]"
                          : "max-h-[min(42vh,360px)]",
                      ].join(" ")}
                    >
                      <p className="flex flex-wrap items-center gap-x-2 mb-2">
                        <span className="text-[#28c840]">charcrith</span>
                        <span className="text-white/40">~</span>
                        <span className="text-[#5ac8fa]">%</span>
                        <span className="text-[#f5f5f5]">
                          {typedCmd}
                          {phase === "typing" && (
                            <span className="ml-0.5 inline-block h-[1.05em] w-[7px] translate-y-[2px] bg-[#d4d4d4] animate-[pulse_1s_steps(1)_infinite]" />
                          )}
                        </span>
                      </p>

                      {(phase === "output" || phase === "done") && (
                        <div className="space-y-1 text-[#f5f5f5] whitespace-pre-wrap break-words">
                          {detail.lines.slice(0, visibleLines).map((line, i) => (
                            <p key={i} className={line === "" ? "h-3" : undefined}>
                              {line || "\u00A0"}
                            </p>
                          ))}
                        </div>
                      )}

                      {phase === "done" && (
                        <p className="flex flex-wrap items-center gap-x-2 mt-3">
                          <span className="text-[#28c840]">charcrith</span>
                          <span className="text-white/40">~</span>
                          <span className="text-[#5ac8fa]">%</span>
                          <span className="ml-0.5 inline-block h-[1.05em] w-[7px] translate-y-[2px] bg-[#d4d4d4] animate-[pulse_1s_steps(1)_infinite]" />
                        </p>
                      )}
                    </div>
                  </MacWindow>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
